/**
 * The organisation's timezone, for rendering scheduled times.
 *
 * A shift is stored as UTC and means a wall-clock time *where the nonprofit
 * is*. Rendering it with the browser's zone gets that right only when the
 * viewer happens to be in the same place: a Memphis rota read from a laptop
 * still set to UTC shows every shift five or six hours out, and worse, some
 * of them on the wrong day.
 *
 * Deliberately scoped to schedule display. Created-at and updated-at
 * timestamps elsewhere in the app still render in the viewer's own zone,
 * which is the right default for "when did this happen to me". It is the
 * roster - "be here Tuesday at 9" - that belongs to the organisation.
 */
import { useGetCurrentManifestQuery } from "@/features/manifest/api/manifestApi";

/** Used when the manifest has not loaded yet, or predates the field. */
const FALLBACK = "America/Chicago";

export function useTenantTimezone(): string {
  const { data } = useGetCurrentManifestQuery();
  return data?.timezone || FALLBACK;
}

/**
 * The calendar date an instant falls on, in `timeZone`, as "YYYY-MM-DD".
 *
 * Bucketing shifts into day columns has to happen in the org's zone, not the
 * viewer's: an 8pm Tuesday shift in Chicago is Wednesday in UTC, and a
 * calendar that files it under Wednesday is simply wrong.
 *
 * en-CA because it formats as YYYY-MM-DD, which sorts and compares as text.
 */
export function zonedDateKey(iso: string | Date, timeZone: string): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    // An unknown zone name would throw on every render. Falling back to the
    // viewer's zone shows a plausible calendar rather than a blank page.
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  }
}

/** How far `timeZone` is from UTC at a given instant, in milliseconds. */
function offsetMs(timeZone: string, at: Date): number {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(at);
  } catch {
    return -at.getTimezoneOffset() * 60_000;
  }
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  // Hour 24 appears at midnight in some locales' 2-digit hour output.
  const hour = get("hour") % 24;
  const asIfUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    hour,
    get("minute"),
    get("second"),
  );
  return asIfUtc - at.getTime();
}

/**
 * Read a picked date/time as the ORGANISATION's wall clock and return the
 * instant it names, as an ISO string.
 *
 * Date pickers hand back a value in the browser's zone: type "6:00 PM" on a
 * laptop in London and you get 18:00 London, which is midday in Memphis. But
 * "the event starts at 6" always means 6 where the event is - and this page
 * renders every time in the org's zone, so without this the form and the
 * list disagree by the offset between them.
 *
 * Two passes so a time that lands near a DST switch resolves against the
 * offset actually in force at that instant, not the one before it.
 */
export function wallClockToIso(local: Date, timeZone: string): string {
  const wall = Date.UTC(
    local.getFullYear(),
    local.getMonth(),
    local.getDate(),
    local.getHours(),
    local.getMinutes(),
    local.getSeconds(),
  );
  let instant = wall - offsetMs(timeZone, new Date(wall));
  instant = wall - offsetMs(timeZone, new Date(instant));
  return new Date(instant).toISOString();
}

/**
 * The mirror of `wallClockToIso`: an instant in, a browser-zone Date whose
 * wall clock reads as the org's out. Feed this to a date picker so it shows
 * "6:00 PM" for an event that starts at 6pm where the org is, wherever the
 * person filling in the form happens to be.
 */
export function isoToWallClock(iso: string | Date, timeZone: string): Date {
  const at = typeof iso === "string" ? new Date(iso) : iso;
  // Built through the LOCAL Date constructor on purpose. Date pickers read
  // local fields, so shifting the underlying timestamp is not enough - the
  // local reading has to be the org's wall clock, or a viewer one hour east
  // sees 8pm for a 7pm event.
  const shifted = new Date(at.getTime() + offsetMs(timeZone, at));
  return new Date(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth(),
    shifted.getUTCDate(),
    shifted.getUTCHours(),
    shifted.getUTCMinutes(),
    shifted.getUTCSeconds(),
  );
}

/** "9am" / "9:30am" for an instant, in `timeZone`. */
export function zonedTimeLabel(iso: string, timeZone: string): string {
  const d = new Date(iso);
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).formatToParts(d);
  } catch {
    parts = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).formatToParts(d);
  }
  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? "";
  const hour = get("hour");
  const minute = get("minute");
  const period = get("dayPeriod").toLowerCase();
  // Drop ":00" - a rota reads better as "9am to 1pm" than "9:00am to 1:00pm".
  return minute === "00" ? `${hour}${period}` : `${hour}:${minute}${period}`;
}
